<template>
  <div id="recaptcha-wrapper" style="display: none"></div>
  <div class="modal PhoneVerificationModal">
    <div class="modal--backdrop"></div>
    <div class="modal--content">
      <div class="modal--content-body">
        <div class="HeadingtextinfoDv">
          <h2>
            {{
              $t("setting.verification.title", {
                type: twoFaPhoneVerifictaion
                  ? $t("setting.common.phoneNumber")
                  : $t("setting.twoFactorAuth.title"),
              })
            }}
          </h2>
          <p v-if="phone && !isLoading">
            {{
              $t("setting.verification.subTitlePhone", {
                phone: phone,
              })
            }}
          </p>
        </div>
        <div v-if="isLoading" class="verificationLoader">
          <div class="loader"></div>
        </div>
        <template v-else>
          <div class="inputWithLabelDv">
            <v-otp-input :is-disabled="isTimeoutLessThanOne" ref="otpInput" class="otpContainer leftinfoPhoneDv"
              v-model:value="bindModal" input-classes="otp-input" separator="" :num-inputs="6" :should-auto-focus="true"
              input-type="letter-numeric" :conditionalClass="['one', 'two', 'three', 'four']" :placeholder="[]"
              @on-complete="handleOnComplete" />
          </div>
          <el-row class="code-resend" v-if="timeout != -1">
            <el-col :span="24">
              <p>
                {{ $t("setting.verification.codeExpire") }}
                <span>{{
                  $t("setting.verification.seconds", { count: timeout })
                }}</span>
              </p>
            </el-col>
            <el-col :span="24">
              <a :class="{ disabled: timeout > 0 }" @click="sendCode()">{{
                $t("setting.verification.resendCode")
              }}</a>
            </el-col>
          </el-row>
          <div class="btninfoDv">
            <button class="btn btn--primary" v-if="isLoading">
              <div class="loader"></div>
            </button>
            <button class="btn btn--primary" @click="verifyCode()" v-else>
              {{ $t("setting.common.verify") }}
            </button>
            <button class="btn btn--outline" :disabled="isLoading" @click="dismiss()">
              {{ $t("common.actions.cancel") }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import IconComponent from "@/components/IconComponent.vue";
import twoFaPopup from "./GetTwoFaPhone.vue";
import { VerifyFirebaseToken } from "@/helpers/models/verify_firebase";
import firebaseService from "@/services/firebase.service";
import httpService from "@/services/http.service";
import storageService from "@/services/storage.service";
import utilService from "@/services/util.service";
import { getAuth, RecaptchaVerifier, User } from "firebase/auth";
import { computed, defineComponent, onMounted, ref } from "vue";
import { TYPE } from "vue-toastification";
import VOtpInput from "vue3-otp-input";
import { openModal, popModal, Modal } from "jenesius-vue-modal";
import { twoFactAuthComposable } from "../../twoFactorAuth-composables";
import { useI18n } from "vue-i18n";

export default defineComponent({
  props: {
    userData: Object,
    is2FaAuth: {
      type: Boolean,
      default: false,
    },
    twoFaPhoneVerifictaion: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    VOtpInput,
    IconComponent,
  },
  emits: [Modal.EVENT_PROMPT],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { twoFaAuthenticated } = twoFactAuthComposable();
    const isLoading = ref<boolean>(false);
    const timeout = ref<number>(-1);
    const phone = ref<string>("");

    const dismiss = () => {
      emit(Modal.EVENT_PROMPT, "close");
      popModal();
    };
    const user = ref<any>();
    const code = ref<string>("");

    onMounted(async () => {
      user.value = storageService.getUser();

      if (props.userData) {
        phone.value = `${props.userData!.phoneNumber!.slice(
          0,
          4
        )}****${props.userData!.phoneNumber!.slice(
          props.userData!.phoneNumber?.length! - 2,
          props.userData!.phoneNumber?.length
        )}`;
        sendCode();
      }
    });

    const isTimeoutLessThanOne = computed(() => {
      return timeout.value < 1;
    });

    const startTimer = () => {
      if (timeout.value > 0) {
        timeout.value--;
        setTimeout(() => {
          startTimer();
        }, 1000);
      }
    };

    const isRecaptchaVerified = ref(false);
    const recaptchaId = ref("verify-btn");
    const sendCode = async () => {
      isLoading.value = true;

      if (timeout.value < 1) {
        try {
          const gwin = window as any;
          const auth = getAuth();

          if (gwin.recaptchaVerifier) {
            try {
              gwin.recaptchaVerifier.clear();
            } catch (e) {
              console.warn("recaptchaVerifier.clear() failed:", e);
            }
            gwin.recaptchaVerifier = null;
          }

          const wrapper = document.getElementById("recaptcha-wrapper");
          if (wrapper) {
            wrapper.innerHTML = "";
          }

          recaptchaId.value = `verify-btn-${Date.now()}`;
          const newRecaptchaDiv = document.createElement("div");
          newRecaptchaDiv.setAttribute("id", recaptchaId.value);
          newRecaptchaDiv.style.display = "none";
          wrapper?.appendChild(newRecaptchaDiv);

          gwin.recaptchaVerifier = new RecaptchaVerifier(
            recaptchaId.value,
            {
              size: "invisible",
              callback: () => {
                isRecaptchaVerified.value = true;
              },
              "expired-callback": () => {
                gwin.recaptchaVerifier = null;
                isRecaptchaVerified.value = false;
              },
            },
            auth
          );

          try {
            await gwin.recaptchaVerifier.render();
            await gwin.recaptchaVerifier.verify();
          } catch (error) {
            console.log("reCAPTCHA rendering error:", error);
          }

          const response = await firebaseService.sendOTPCode(
            props.userData!.phoneNumber!
          );

          if (isRecaptchaVerified.value || response) {
            timeout.value = 60;
            startTimer();
          }

          isLoading.value = false;
        } catch (e) {
          console.error("Error sending OTP:", e);
          isLoading.value = false;
          timeout.value = 60;
          startTimer();
        }
      }
    };

    const verifyCode = () => {
      if (code.value.length == 6) {
        isLoading.value = true;
        var gwin = window as any;
        gwin.confirmationResult
          .confirm(code.value)
          .then((result: any) => {
            if (props.twoFaPhoneVerifictaion) {
              onVerification(result);
            } else {
              emit(Modal.EVENT_PROMPT, { userData: props.userData });
              twoFaAuthenticated.value = true;
            }
          })
          .catch((error: any) => {
            isLoading.value = false;
            utilService.showToast(
              t("setting.verification.toast.invalidCode"),
              TYPE.ERROR
            );
          });
      } else {
        utilService.showToast(
          t("setting.verification.toast.enterCode"),
          TYPE.ERROR
        );
      }
    };

    const onVerification = (otpResult: any) => {
      try {
        var verifyPhoneNumber = new VerifyFirebaseToken(
          otpResult.user.accessToken,
          otpResult.user.uid,
          user.value!._id!
        );
        httpService.verifyPhoneNumber(verifyPhoneNumber).then((res: any) => {
          isLoading.value = false;
          if (res.isSuccess) {
            res.data.user.phoneNumber = props.userData!.phoneNumber!;
            if (!props.is2FaAuth) {
              storageService.setUser(res.data.user);
            }
            props.userData!.isPhoneVerified = true;
            utilService.showToast(
              t("setting.verification.toast.phoneVerifiedSuccess")
            );
            if (props.is2FaAuth) {
              emit(Modal.EVENT_PROMPT, { userData: props.userData });
            }
            dismiss();
          } else {
            utilService.showToast(
              t("setting.verification.toast.phoneVerifyError"),
              TYPE.ERROR
            );
          }
        });
      } catch (e) {
        isLoading.value = false;
        utilService.showToast(
          t("setting.verification.toast.phoneVerifyError"),
          TYPE.ERROR
        );
      }
    };

    const handleOnComplete = (value: string) => {
      code.value = value;
      verifyCode();
    };

    const openTwoFa = async () => {
      var modal = await openModal(twoFaPopup, { userData: props.userData });
      modal.onclose = () => { };
    };

    return {
      code,
      dismiss,
      handleOnComplete,
      phone,
      isLoading,
      sendCode,
      verifyCode,
      user,
      timeout,
      isTimeoutLessThanOne,
      recaptchaId,
    };
  },
});
</script>
<style lang="scss" scoped>
.PhoneVerificationModal {
  .loader {
    width: 26px;
    height: 26px;
    border-width: 3px;
  }
  .verificationLoader {
    width: 100%;
    height: calc(100% - 60px);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal--content {
    max-height: 450px;
    cursor: default;
    .modal--content-body {
      padding: 30px;
      min-height: 100%;

      .HeadingtextinfoDv {
        h2 {
          font-size: var(--font-2xl);
          color: var(--text-color);
          text-align: center;
          margin: 0px;
          padding: 0px;
          line-height: 1;
          letter-spacing: 0px;
          font-weight: 500;
        }
        p {
          font-size: var(--font-1xs);
          color: var(--text-color);
          text-align: center;
          margin: 15px 0px 0px;
          padding: 0px;
          line-height: 1.6;
          letter-spacing: 0px;
          font-weight: 400;
          opacity: 0.7;
        }
      }
      .inputWithLabelDv {
        margin: 20px 0px;
      }
      .resendDv {
        p {
          font-size: var(--font-sm);
          color: var(--text-color);
          opacity: 0.3;
          cursor: pointer;
          text-align: center;
          margin: 0px 0px 20px;
          padding: 0px;
          letter-spacing: 0px;
          line-height: 1;
        }
      }
      .btninfoDv {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        flex-direction: column;
        .btn {
          font-size: var(--font-2sm);
          width: 370px;
          max-width: 370px;
          margin: 0px;
        }
      }
      .code-resend {
        margin: 0px 0px 20px;
        p {
          margin: 0;
          text-align: center;
          font-size: var(--font-xs);
          font-weight: 400;
          span {
            color: var(--secondary-color);
          }
        }
        a {
          text-align: center;
          margin: 0 auto;
          display: block;
          margin-top: 20px;
          color: var(--text-color);
          cursor: pointer;
          font-size: var(--font-xs);
          font-weight: 500;
          &.disabled {
            opacity: 0.5;
            pointer-events: none;
            cursor: default;
          }
        }
      }
    }
  }
}
@media screen and (max-width: 767px) {
  .PhoneVerificationModal
    .modal--content
    .modal--content-body
    .HeadingtextinfoDv
    h2 {
    line-height: 1.6 !important;
  }
}
</style>
